import type {StateCreator} from 'zustand'
import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import {produce} from 'immer'

export type DiffSummariesSlice = {
  diffSummaries: PullRequestFileTreeDiff[]
  diffSummariesActions: {
    setDiffSummaries: (diffSummaries?: PullRequestFileTreeDiff[]) => void
    setDiffViewed: (path: string, viewed: boolean) => void
    onCommentThreadDeleted: ({path, threadID}: {path: string; threadID: string}) => void
    onCommentThreadAdded: ({
      path,
      diffLineKey,
      startPosition,
      threadID,
    }: {
      path: string
      diffLineKey: string
      startPosition: string | undefined
      threadID: string
    }) => void
  }
}

export const createDiffSummariesSlice =
  (): StateCreator<DiffSummariesSlice, [['zustand/devtools', never]], [], DiffSummariesSlice> => (set, _get) => ({
    diffSummaries: [],
    codeowners: undefined,
    diffSummariesActions: {
      setDiffSummaries: (diffSummaries?: PullRequestFileTreeDiff[]) => {
        set({diffSummaries}, undefined, 'updateDiffSummaries')
      },
      setDiffViewed: (path: string, viewed: boolean) => {
        set(state => {
          if (!state.diffSummaries) return state
          return {
            diffSummaries: state.diffSummaries.map(diffSummary => {
              if (diffSummary.path === path) {
                return {...diffSummary, markedAsViewed: viewed}
              }

              return diffSummary
            }),
          }
        })
      },
      onCommentThreadAdded: ({
        path,
        diffLineKey,
        startPosition,
        threadID,
      }: {
        path: string
        diffLineKey: string
        startPosition: string | undefined
        threadID: string
      }) => {
        set(
          produce((state: DiffSummariesSlice) => {
            if (!state.diffSummaries) return

            const diff = state.diffSummaries.find(summary => summary.path === path)
            if (!diff) return

            const markersMap = diff.markersMap ?? {}
            markersMap[diffLineKey] ||= {
              threads: [],
              annotations: [],
            }
            const markersDiffLine = markersMap[diffLineKey]
            markersDiffLine.threads.push({
              id: parseInt(threadID),
              start: startPosition,
            })
            diff.totalCommentsCount = (diff.totalCommentsCount || 0) + 1
          }),
          undefined,
          'commentAddedForDiffSummary',
        )
      },
      onCommentThreadDeleted: ({path, threadID}: {path: string; threadID: string}) => {
        set(
          produce((state: DiffSummariesSlice) => {
            if (!state.diffSummaries) return

            const diff = state.diffSummaries.find(summary => summary.path === path)
            if (!diff) return
            if (!diff?.totalCommentsCount || !diff?.markersMap) return

            for (const [markerID, marker] of Object.entries(diff.markersMap)) {
              marker.threads = marker.threads.filter(thread => thread.id !== parseInt(threadID))

              if (marker.threads.length === 0 && marker.annotations.length === 0) {
                delete diff.markersMap[markerID]
              }
            }
          }),
          undefined,
          'commentDeletedForDiffSummary',
        )
      },
    },
  })
